/**
 * 🎣 REACT HOOKS FOR FEATURE ACCESS
 *
 * Fortune 500 Pattern: Client-Side Feature Checks with Server Validation
 * Used by: Vercel Dashboard, GitHub UI, Linear App
 *
 * USAGE:
 * ```tsx
 * const { hasFeature, checkLimit } = useFeatureAccess();
 *
 * if (hasFeature(FeatureFlag.CALENDAR_INTEGRATION)) {
 *   return <CalendarSettings />;
 * }
 *
 * return <UpgradePrompt feature="Calendar Integration" />;
 * ```
 */

'use client';

import React, { useContext, createContext, useState, useEffect, useCallback } from 'react';
import { FeatureFlag, FeatureLimits, PlanFeatures } from '@/lib/features/config';
import { useToast } from './use-toast';

/**
 * Feature Access Context Type
 */
interface FeatureAccessContextType {
  // Current plan
  currentPlan: string | null;
  planName: string | null;
  planFeatures: FeatureFlag[];
  planLimits: FeatureLimits | null;

  // Feature checks
  hasFeature: (feature: FeatureFlag) => boolean;
  requireFeature: (feature: FeatureFlag) => boolean;

  // Limit checks
  checkLimit: (limitType: keyof FeatureLimits, currentUsage: number) => {
    allowed: boolean;
    limit: number | 'unlimited';
    usage: number;
    percentage: number;
  };

  // Grace period
  isInGracePeriod: boolean;
  gracePeriodEndsAt: Date | null;

  // Trial Status
  isTrialExpired: boolean;
  trialEndsAt: Date | null;
  trialDaysRemaining: number | null;
  storefrontBlocked: boolean;
  upgradePlan: string | null;

  // Loading state
  loading: boolean;

  // Actions
  refreshFeatures: () => Promise<void>;
}

const FeatureAccessContext = createContext<FeatureAccessContextType | undefined>(undefined);

/**
 * Provider Component
 */
export function FeatureAccessProvider({
  children,
  tenantId,
}: {
  children: React.ReactNode;
  tenantId: string | null;
}) {
  const [currentPlan, setCurrentPlan] = useState<string | null>(null);
  const [planName, setPlanName] = useState<string | null>(null);
  const [planFeatures, setPlanFeatures] = useState<FeatureFlag[]>([]);
  const [planLimits, setPlanLimits] = useState<FeatureLimits | null>(null);
  const [isInGracePeriod, setIsInGracePeriod] = useState(false);
  const [gracePeriodEndsAt, setGracePeriodEndsAt] = useState<Date | null>(null);
  const [isTrialExpired, setIsTrialExpired] = useState(false);
  const [trialEndsAt, setTrialEndsAt] = useState<Date | null>(null);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState<number | null>(null);
  const [storefrontBlocked, setStorefrontBlocked] = useState(false);
  const [upgradePlan, setUpgradePlan] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  /**
   * Fetch subscription and features from API.
   *
   * Gated on `tenantId` — bailing out early when null skips ~2 wasted
   * round-trips per admin-page load (the old sentinel `admin-check`
   * crashed the server's uuid cast for non-admin users, then bounced
   * back through the trial fallback). The server resolves the primary
   * tenant from `tenant_user_roles` when no `tenant_id` is passed, so
   * we don't need a client-side sentinel anymore.
   */
  const refreshFeatures = useCallback(async () => {
    if (!tenantId) {
      // Stay in `loading` until the ActiveTenantProvider hands us a
      // real tenantId. Admin layout already gates rendering on that.
      return;
    }
    try {
      setLoading(true);
      const response = await fetch(`/api/features/check?tenant_id=${tenantId}`);
      const data = await response.json();

      if (data.success) {
        // SSOT: `/api/features/check` reads from `subscription_plans`
        // (Stripe-mirrored) via plans-db.ts. `features` + `limits` are
        // always returned by the server, so no client-side catalog
        // lookup is needed anymore.
        setCurrentPlan(data.plan);
        setPlanName(data.planName || null);
        setPlanFeatures(data.features || []);
        setPlanLimits(data.limits || null);
        setIsInGracePeriod(data.gracePeriod || false);
        setGracePeriodEndsAt(data.gracePeriodEndsAt ? new Date(data.gracePeriodEndsAt) : null);

        // Trial Status
        setIsTrialExpired(data.trialExpired || false);
        setTrialEndsAt(data.trialEndsAt ? new Date(data.trialEndsAt) : null);
        setTrialDaysRemaining(data.trialDaysRemaining ?? null);
        setStorefrontBlocked(data.storefrontBlocked || false);
        setUpgradePlan(data.upgradePlan || null);
      } else {
        // API explicit failure → deny by default. Better lock the user
        // out for one render-cycle than accidentally grant access from
        // a stale client-side catalog while the real plan is unknown.
        setCurrentPlan(null);
        setPlanName(null);
        setPlanFeatures([]);
        setPlanLimits(null);
        setIsTrialExpired(false);
        setStorefrontBlocked(false);
      }
    } catch (error) {
      console.error('Failed to fetch features:', error);
      // Network error → same deny-by-default. `refreshFeatures` is
      // exposed so the caller can retry once the connection recovers.
      setCurrentPlan(null);
      setPlanName(null);
      setPlanFeatures([]);
      setPlanLimits(null);
      setIsTrialExpired(false);
      setStorefrontBlocked(false);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  // Load features on mount and when tenantId changes
  useEffect(() => {
    refreshFeatures();
  }, [refreshFeatures]);

  /**
   * Check if tenant has access to feature
   */
  const hasFeature = useCallback(
    (feature: FeatureFlag): boolean => {
      if (!currentPlan) return false;
      return planFeatures.includes(feature);
    },
    [currentPlan, planFeatures]
  );

  /**
   * Require feature (throws toast if not available)
   */
  const requireFeature = useCallback(
    (feature: FeatureFlag): boolean => {
      const available = hasFeature(feature);

      if (!available) {
        toast({
          variant: 'destructive',
          title: 'Upgrade Required',
          description: `This feature requires a ${getMinimumPlanName(feature)} plan or higher.`,
        });
      }

      return available;
    },
    [hasFeature, toast]
  );

  /**
   * Check if within usage limits
   */
  const checkLimit = useCallback(
    (limitType: keyof FeatureLimits, currentUsage: number) => {
      if (!planLimits) {
        return {
          allowed: false,
          limit: 0,
          usage: currentUsage,
          percentage: 100,
        };
      }

      const limit = planLimits[limitType];

      if (limit === 'unlimited') {
        return {
          allowed: true,
          limit: 'unlimited' as const,
          usage: currentUsage,
          percentage: 0,
        };
      }

      const percentage = (currentUsage / limit) * 100;

      return {
        allowed: currentUsage < limit,
        limit,
        usage: currentUsage,
        percentage,
      };
    },
    [planLimits]
  );

  const value: FeatureAccessContextType = {
    currentPlan,
    planName,
    planFeatures,
    planLimits,
    hasFeature,
    requireFeature,
    checkLimit,
    isInGracePeriod,
    gracePeriodEndsAt,
    isTrialExpired,
    trialEndsAt,
    trialDaysRemaining,
    storefrontBlocked,
    upgradePlan,
    loading,
    refreshFeatures,
  };

  return React.createElement(
    FeatureAccessContext.Provider,
    { value },
    children
  );
}

/**
 * Hook to access feature context
 */
export function useFeatureAccess(): FeatureAccessContextType {
  const context = useContext(FeatureAccessContext);

  if (context === undefined) {
    throw new Error('useFeatureAccess must be used within FeatureAccessProvider');
  }

  return context;
}

/**
 * Helper: Best-effort name for the upgrade target shown in the
 * "feature requires upgrade" toast.
 *
 * Was a hardcoded lookup over PLAN_FEATURES — that catalog now lives in
 * Stripe + DB, not on the client. Toast copy is non-load-bearing, so we
 * use 'Professional' as a sensible default. The actual gate is enforced
 * server-side in `/api/features/check` (which knows the real catalog).
 */
function getMinimumPlanName(_feature: FeatureFlag): string {
  return 'Professional';
}

/**
 * 🎯 SHORTCUT HOOK: Check single feature
 *
 * USAGE:
 * ```tsx
 * const hasCalendar = useFeature(FeatureFlag.CALENDAR_INTEGRATION);
 *
 * if (!hasCalendar) {
 *   return <UpgradePrompt />;
 * }
 * ```
 */
export function useFeature(feature: FeatureFlag): boolean {
  const { hasFeature } = useFeatureAccess();
  return hasFeature(feature);
}

/**
 * 🚨 SHORTCUT HOOK: Require feature (with toast)
 */
export function useRequireFeature(feature: FeatureFlag): boolean {
  const { requireFeature } = useFeatureAccess();
  return requireFeature(feature);
}
